import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { FolderLock, Upload, Link2, Trash2, ExternalLink, Download, Sparkles } from "lucide-react";

/**
 * Ring-fenced sponsor materials, drop-boxed onto the team by the assigned
 * mentor or an admin. Team members can view + download; only mentor/admin
 * can add or delete.
 */
export default function TeamResourcesCard({ teamId, mentorAssigned }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // link form
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  // file form
  const [fileTitle, setFileTitle] = useState("");
  const [fileDesc, setFileDesc] = useState("");
  const [file, setFile] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "faculty";
  const isMentorForTeam = user?.role === "mentor" && mentorAssigned;
  const canUpload = isAdmin || isMentorForTeam;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/resources`);
      setItems(data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (teamId) load(); }, [teamId]);

  const addLink = async (e) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) return toast.error("Title and URL required");
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/resources`, {
        title: title.trim(),
        external_url: url.trim(),
        description: description.trim() || null,
      });
      toast.success("Link added");
      setTitle(""); setUrl(""); setDescription("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add link");
    } finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", fileTitle.trim() || file.name);
      fd.append("description", fileDesc.trim());
      await api.post(`/teams/${teamId}/resources/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setFile(null); setFileTitle(""); setFileDesc("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload file");
    } finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!(await confirm({ title: `Delete "${r.title}"?`, destructive: true }))) return;
    try {
      await api.delete(`/teams/${teamId}/resources/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="border border-[#0A0A0A] bg-white p-6 space-y-5" data-testid="team-resources-card">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><FolderLock size={12} /> Team resources · private</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Sponsor materials for your team</h2>
        <p className="text-xs text-[#525252] mt-2 max-w-2xl">
          Ring-fenced to <strong>your team</strong>. Only your mentor and admins can drop files or links in here —
          everyone on the team can view and download. Use it for sponsor briefs, background reports and anything
          the wider cohort shouldn&apos;t see.
        </p>
      </div>

      {canUpload && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <form onSubmit={addLink} className="border border-[#E5E7EB] p-4 space-y-3" data-testid="team-resource-link-form">
            <div className="label-eyebrow flex items-center gap-1.5"><Link2 size={11} /> Add a link</div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (e.g. Sponsor's annual report)"
              className="rounded-none h-11"
              data-testid="team-resource-link-title"
            />
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className="rounded-none h-11"
              data-testid="team-resource-link-url"
            />
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes for the team"
              className="rounded-none min-h-[60px]"
              data-testid="team-resource-link-desc"
            />
            <Button type="submit" disabled={busy} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" data-testid="team-resource-link-btn">
              {busy ? "Saving…" : "Add link"}
            </Button>
          </form>

          <form onSubmit={uploadFile} className="border border-[#E5E7EB] p-4 space-y-3" data-testid="team-resource-upload-form">
            <div className="label-eyebrow flex items-center gap-1.5"><Upload size={11} /> Upload a file</div>
            <Input
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="File title (optional)"
              className="rounded-none h-11"
              data-testid="team-resource-file-title"
            />
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm border border-[#E5E7EB] p-2"
              data-testid="team-resource-file-input"
            />
            <Textarea
              value={fileDesc}
              onChange={(e) => setFileDesc(e.target.value)}
              placeholder="Optional notes for the team"
              className="rounded-none min-h-[60px]"
              data-testid="team-resource-file-desc"
            />
            <p className="text-[11px] text-[#525252]">
              Up to 20 MB. Sponsor briefs, background reports, decks — anything just this team should see.
            </p>
            <Button type="submit" disabled={busy || !file} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="team-resource-upload-btn">
              <Upload size={14} className="mr-1.5" /> {busy ? "Uploading…" : "Upload"}
            </Button>
          </form>
        </div>
      )}

      {!canUpload && (
        <div className="text-xs text-[#525252] italic flex items-center gap-2 border border-[#E5E7EB] p-3 bg-[#F9FAFB]" data-testid="team-resource-readonly-note">
          <Sparkles size={12} className="text-[#25AAAA]" /> Read-only — your mentor and the programme admins add materials here.
        </div>
      )}

      <div>
        {loading ? (
          <div className="text-sm text-[#525252]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#525252] italic" data-testid="team-resources-empty">
            Nothing here yet. Your mentor will drop sponsor materials in as they come through.
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E7EB]" data-testid="team-resources-list">
            {items.map((r) => (
              <li key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`team-resource-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider bg-[#0F1F2E] text-white px-1.5 py-0.5 font-bold">
                      {r.kind === "file" ? "File" : "Link"}
                    </span>
                    <div className="font-medium text-[#0F1F2E]">{r.title}</div>
                  </div>
                  {r.description && (
                    <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap mt-2 leading-relaxed">{r.description}</p>
                  )}
                  {r.kind === "link" && r.external_url && (
                    <a href={r.external_url} target="_blank" rel="noreferrer"
                       className="text-sm text-[#2596be] hover:underline mt-2 inline-flex items-center gap-1"
                       data-testid={`team-resource-open-${r.id}`}>
                      <ExternalLink size={12} /> Open link
                    </a>
                  )}
                  {r.kind === "file" && r.file_url && (
                    <a href={r.file_url} target="_blank" rel="noreferrer"
                       className="text-sm text-[#2596be] hover:underline mt-2 inline-flex items-center gap-1"
                       data-testid={`team-resource-download-${r.id}`}>
                      <Download size={12} /> {r.file_name || "Download"} ({Math.round((r.file_size || 0) / 1024)} KB)
                    </a>
                  )}
                  <div className="text-[11px] uppercase tracking-wider text-[#525252] mt-2">
                    {r.author_name} · {r.author_role} · {new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                {(isAdmin || r.author_id === user?.id) && (
                  <button onClick={() => del(r)} className="text-[#A23232] hover:text-[#7A1F1F]" data-testid={`team-resource-delete-${r.id}`} title="Delete">
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
